import { useLocation } from '@tanstack/react-router';
import { useEffect } from 'react';

import { tGlobal } from './../services/translation';

let customTitle: string | null = null;

function getMetaTitle(pathname: string): string {
  // Handle organization settings subpages
  if (pathname.startsWith('/app/settings/organization/')) {
    const parts = pathname.split('/');
    const subpage = parts[5];
    let title = '';
    switch (subpage) {
      case 'billing':
        title = tGlobal('metaTitleSettingsBilling');
        break;
      case 'users':
        title = tGlobal('metaTitleSettingsUsers');
        break;
      case 'brands':
        title = tGlobal('metaTitleSettingsBrands');
        break;
      case 'credits':
        title = tGlobal('metaTitleSettingsCredits');
        break;
      default:
        title = tGlobal('metaTitleSettings');
    }
    return title + ' | Blym';
  }

  // Handle funnel subpages
  if (pathname.startsWith('/app/funnel/')) {
    const parts = pathname.split('/');
    const subpage = parts[4];
    let title = '';
    switch (subpage) {
      case 'title':
        title = tGlobal('metaTitleFunnelChooseTitle');
        break;
      case 'outlines':
        title = tGlobal('metaTitleFunnelChooseOutline');
        break;
      case 'customize-content':
        title = tGlobal('metaTitleFunnelAddContent');
        break;
      case 'content':
        title = tGlobal('metaTitleFunnelAddContent');
        break;
      default:
        title = tGlobal('metaTitleFunnel');
    }
    return title + ' | Blym';
  }

  // Handle other pages
  const routes: { prefix: string; title: string }[] = [
    { prefix: '/auth/demo', title: tGlobal('metaTitleDemo') },
    { prefix: '/app/blogs', title: tGlobal('metaTitleBlogDashboard') },
    { prefix: '/app/products/bulk-import', title: tGlobal('metaTitleBulkImports') },
    { prefix: '/app/products/templates', title: tGlobal('metaTitleProductTemplates') },
    { prefix: '/app/product-template/new', title: tGlobal('metaTitleNewProductTemplate') },
    { prefix: '/app/products', title: tGlobal('metaTitleProductDashboard') },
    { prefix: '/app/brand-context', title: tGlobal('metaTitleBrandContext') },
    { prefix: '/app/content/', title: tGlobal('metaTitleEditor') },
    { prefix: '/app/settings/users', title: tGlobal('metaTitleSettingsProfile') },
    { prefix: '/app/funnel/add-extra-info', title: tGlobal('metaTitleFunnelAddExtraInfo') },
    { prefix: '/app/funnel/choose-title', title: tGlobal('metaTitleFunnelChooseTitle') },
    { prefix: '/app/funnel/choose-outline', title: tGlobal('metaTitleFunnelChooseOutline') },
    { prefix: '/app/funnel', title: tGlobal('metaTitleFunnel') },
    { prefix: '/auth/signup', title: tGlobal('metaTitleSignUp') },
    { prefix: '/auth/signin', title: tGlobal('metaTitleSignIn') },
    { prefix: '/app', title: tGlobal('metaTitleDashboard') },
  ];

  const match = routes.find(function (route) {
    return pathname.startsWith(route.prefix);
  });
  if (match != null) {
    return `${match.title} | Blym`;
  }

  return 'Blym';
}

export function setCustomMetaTitle(title: string | null, showEditor: boolean = true): void {
  customTitle = title;
  // Update the title immediately
  if (title !== null) {
    document.title = `${title} ${showEditor ? `- ${tGlobal('metaTitleEditor')}` : ''} | Blym`;
  } else {
    // Reset to default title based on current path
    const { pathname } = window.location;
    document.title = getMetaTitle(pathname);
  }
}

function getMetaDescription(pathname: string): string {
  const routes: { prefix: string; description: string }[] = [
    { prefix: '/auth/demo', description: tGlobal('metaDescriptionDemo') },
    { prefix: '/auth/signup', description: tGlobal('metaDescriptionSignUp') },
    { prefix: '/auth/signin', description: tGlobal('metaDescriptionSignIn') },
  ];

  const match = routes.find(function (route) {
    return pathname.startsWith(route.prefix);
  });

  return match?.description ?? '';
}

export function MetaTitle() {
  const { pathname } = useLocation();

  useEffect(
    function () {
      if (pathname === '/app/content/' && customTitle !== null) {
        return;
      }
      const title = getMetaTitle(pathname);
      const description = getMetaDescription(pathname);
      document.title = title;
      if (description === '') {
        return;
      }
      if (document.querySelector('meta[name="description"]') == null) {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = description;
        document.head.appendChild(meta);
        return;
      }

      document.querySelector('meta[name="description"]')?.setAttribute('content', description);
    },
    [pathname]
  );

  return null;
}
